import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { Logger } from 'src/app/common/logging/logger';
import { AttachmentDto } from 'src/app/shared/models/attachment/attachment-dto.model';
import { Database } from 'src/app/shared/services/attachment/Database';
import {
  AttachmentDatabaseService,
  EnhancedAttachmentDto,
} from 'src/app/shared/services/attachment/attachment-database.service';
import { arrayBufferToBlob, prepareDataForUpload } from 'src/app/shared/services/attachment/attachment-helper';
import { FeathersService } from 'src/app/shared/services/feathers.service';
import { NetworkInfoService } from '../network/network-info.service';
import { IAttachmentTransferService } from './attachment-transfer-service.interface';

export interface OfflineUploadResult {
  localId: string;
  type: number;
  id: string;
}

export interface UploadResult {
  loading?: boolean;
  progress?: number;
  error?: Error;
  needRetry?: boolean;
  data?: { id: string };
}

export type UploadData = EnhancedAttachmentDto & {
  /** Binary data to upload.
   * If blob is an ArrayBuffer UploadData.mime has to be defined */
  blob: Blob | ArrayBuffer;
};

@Injectable({ providedIn: 'root' })
export class AttachmentTransferService implements IAttachmentTransferService {
  private _offlineTxSuccess = new Subject<OfflineUploadResult>();

  public get offlineTxSuccess(): Observable<OfflineUploadResult> {
    return this._offlineTxSuccess.asObservable();
  }

  constructor(
    private _logger: Logger,
    private _attachmentDatabaseService: AttachmentDatabaseService,
    private _feathersService: FeathersService,
    private readonly _networkInfoService: NetworkInfoService
  ) {}

  public async upload(
    data: UploadData,
    localId: string,
    /** upload immediately or save in attachmentToSave queue to be uploaded later */
    tryUploadingImmediately: boolean,
    isFromQueue?: boolean
  ): Promise<UploadResult> {
    try {
      if (tryUploadingImmediately === false || this._networkInfoService.isNotConnected) {
        await this._attachmentDatabaseService.set(Database.AttachmentToSave, localId, { data, localId });
        return { needRetry: true, loading: false, data: { id: localId } };
      }
      return await this.onlineUpload(data, localId, isFromQueue);
    } catch (error) {
      this._logger.error('Failed to upload attachment', error);
      const isDuplicatedError = error && error.code && error.code === 409;
      if (isDuplicatedError) {
        this._logger.error('got duplicated attachment error', error);
        await this.handleDuplicateError(localId).catch(_err =>
          this._logger.error('duplicate error cleanup failed', error)
        );
      }
      return { error, needRetry: !isDuplicatedError, loading: false, data: { id: localId } };
    }
  }

  private async onlineUpload(data: UploadData, localId: string, isFromQueue: boolean): Promise<UploadResult> {
    this._logger.info(`online upload ${localId}`);
    const uploadedAttachment = await this.uploadToBackend(data, localId, isFromQueue);
    await this._attachmentDatabaseService.replaceLocalData(localId, uploadedAttachment._id, uploadedAttachment);
    await this._attachmentDatabaseService.replaceLocalUpdate(localId, uploadedAttachment._id);
    if (isFromQueue) {
      this._offlineTxSuccess.next({ localId, type: data.metadata.type, id: uploadedAttachment._id });
    }
    return { loading: false, data: { id: uploadedAttachment._id } };
  }

  private async uploadToBackend(data: UploadData, localId: string, isFromQueue: boolean): Promise<AttachmentDto> {
    this._logger.info(`upload to backend ${localId}`);
    const attachmentService = this._feathersService.getService<AttachmentDto>('attachment');
    try {
      const dataToSend = await prepareDataForUpload(data);
      const result = await attachmentService.create(dataToSend);
      console.log('Attachment Upload result', result);
      return result;
    } catch (error) {
      console.log('Attachment Upload error', error);
      if (!isFromQueue) {
        const clonedData = cloneDeep(data);
        await this._attachmentDatabaseService.set(Database.AttachmentToSave, localId, {
          data: clonedData,
          localId,
        });
      }
      throw error;
    }
  }

  private async handleDuplicateError(localId: string): Promise<void> {
    try {
      await this._attachmentDatabaseService.purge(localId);
    } catch (error) {
      this._logger.error(`handleDuplicateError:remove ${localId}`, error);
    }
  }

  /**
   * Resolves a binary, local-first with write-through:
   * 1. Already in the local blob store → returned, no network.
   * 2. Otherwise downloaded from the server (feathers `attachment.get`, ArrayBuffer) and
   *    *persisted into the local blob store* before returning — a once-viewed attachment is
   *    afterwards available offline (self-healing for missing local blobs).
   *
   * Rejects when the blob is not local and the server is unreachable (socket timeout ~5s)
   * or the attachment does not exist on the server.
   *
   * @param mimeType Content type used to wrap the downloaded ArrayBuffer into a Blob.
   */
  public async fetchBlob(id: string, mimeType: string): Promise<Blob> {
    const keys = await this._attachmentDatabaseService.keys(Database.Blob);
    const blobAlreadyInDb = keys.some(f => f === id);
    if (blobAlreadyInDb) {
      return this._attachmentDatabaseService.get(Database.Blob, id);
    }
    const result = await this._feathersService.getService<ArrayBuffer>('attachment').get(id);
    const blob = arrayBufferToBlob(result, mimeType);
    await this._attachmentDatabaseService.writeBlobToStorage(id, blob);
    return blob;
  }

  public async delete(id: string): Promise<void> {
    const attachmetService = this._feathersService.getService('attachment');
    await attachmetService.remove(id);
  }

  public async update(id: string, data: any): Promise<any> {
    const dataToSend = await prepareDataForUpload(data);

    const attachmentService = this._feathersService.getService('attachment');
    return attachmentService.update(id, dataToSend);
  }
}
